import { ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { formatDateTime } from "@/lib/format";
import { cn } from "@/lib/utils";
import { HITL_EVENT_TYPE, type Notification } from "@/types/notifications";

import { notificationHref } from "./notification-href";

interface InboxRowProps {
  notification: Notification;
  /** Needs-you rows render inline actions; updates render a follow `→`. A
   *  checkpoint shows Approve/Reject; a HITL step shows Mark done / Skip. */
  variant: "needs-you" | "update";
  onApprove: (id: string) => void;
  onReject: (id: string) => void;
  /** "Mark done" / "Skip" for a human-in-the-loop step. */
  onCompleteHitl: (id: string) => void;
  onSkipHitl: (id: string) => void;
  /** Mark read on follow — the count drains as the user works the inbox. */
  onMarkRead: (id: string) => void;
  busy: boolean;
}

export function InboxRow({
  notification,
  variant,
  onApprove,
  onReject,
  onCompleteHitl,
  onSkipHitl,
  onMarkRead,
  busy,
}: InboxRowProps) {
  const navigate = useNavigate();
  const href = notificationHref(
    notification.event_type,
    notification.entity_type,
    notification.entity_id,
  );
  const isHitl = notification.event_type === HITL_EVENT_TYPE;

  const follow = () => {
    if (!notification.read) onMarkRead(notification.id);
    if (href) navigate(href);
  };

  return (
    <div
      className={cn(
        "flex items-start gap-4 px-4 py-3",
        !notification.read && "bg-row-hover",
        notification.read && "opacity-60",
      )}
    >
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-foreground">{notification.title}</p>
        {notification.body && (
          <p className="mt-0.5 text-sm whitespace-pre-line text-muted-foreground">
            {notification.body}
          </p>
        )}
        <p className="mt-1 text-xs text-muted-foreground">
          {formatDateTime(notification.created_at)}
        </p>
      </div>

      {variant === "needs-you" ? (
        <div className="flex shrink-0 items-center gap-2">
          <Button
            size="sm"
            variant="default"
            disabled={busy}
            onClick={() =>
              isHitl
                ? onCompleteHitl(notification.id)
                : onApprove(notification.id)
            }
          >
            {isHitl ? "Mark done" : "Approve"}
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={busy}
            onClick={() =>
              isHitl ? onSkipHitl(notification.id) : onReject(notification.id)
            }
          >
            {isHitl ? "Skip" : "Reject"}
          </Button>
        </div>
      ) : (
        href && (
          <Button
            size="icon"
            variant="ghost"
            className="shrink-0"
            aria-label="Open"
            title="Open"
            onClick={follow}
          >
            <ArrowRight size={15} />
          </Button>
        )
      )}
    </div>
  );
}
